import { COLORS } from '../configs/colors';

/**
 * SLA urgency levels matching CMS1 ruleset.
 *
 * | Urgency | Colour | Condition                                        |
 * |---------|--------|--------------------------------------------------|
 * |    6    | Purple | More than 48 hours remaining                     |
 * |    5    | Blue   | 24-48 hours remaining                            |
 * |    4    | Green  | 16-24 hours remaining                            |
 * |    3    | Yellow | 8-16 hours remaining                             |
 * |    2    | Orange | 4-8 hours remaining                              |
 * |    1    | Red    | Less than 4 hours remaining                      |
 * |    0    | Grey   | Breached, SLA stopped, or SLA met                |
 */

export type SLAUrgencyLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface SLAUrgency {
  level: SLAUrgencyLevel;
  color: string;
  textColor: string;
  label: string;
}

/** WCAG 4.5:1 accessible text variants of each urgency fill color. */
const URGENCY_TEXT_COLORS: Record<SLAUrgencyLevel, string> = {
  6: '#5b3d8f', // dark purple
  5: '#1e5f82', // dark blue
  4: '#3d6b0e', // dark green
  3: '#7a6200', // dark yellow
  2: '#b35e00', // dark orange
  1: '#c40000', // dark red
  0: '#757575', // medium grey
};

const URGENCY_MAP: Record<SLAUrgencyLevel, { color: string; textColor: string; label: string }> = {
  6: { color: COLORS.SLAUrgency6, textColor: URGENCY_TEXT_COLORS[6], label: 'Purple' },
  5: { color: COLORS.SLAUrgency5, textColor: URGENCY_TEXT_COLORS[5], label: 'Blue' },
  4: { color: COLORS.SLAUrgency4, textColor: URGENCY_TEXT_COLORS[4], label: 'Green' },
  3: { color: COLORS.SLAUrgency3, textColor: URGENCY_TEXT_COLORS[3], label: 'Yellow' },
  2: { color: COLORS.SLAUrgency2, textColor: URGENCY_TEXT_COLORS[2], label: 'Orange' },
  1: { color: COLORS.SLAUrgency1, textColor: URGENCY_TEXT_COLORS[1], label: 'Red' },
  0: { color: COLORS.SLAUrgency0, textColor: URGENCY_TEXT_COLORS[0], label: 'Grey' },
};

/**
 * Given remaining minutes, return the CMS1 urgency level, color, and label.
 * Negative or zero remaining minutes return urgency 0 (breached/grey).
 */
export const getSLAUrgency = (remainingMinutes: number): SLAUrgency => {
  if (remainingMinutes <= 0) return { level: 0, ...URGENCY_MAP[0] };
  if (remainingMinutes < 240) return { level: 1, ...URGENCY_MAP[1] }; // <4h
  if (remainingMinutes < 480) return { level: 2, ...URGENCY_MAP[2] }; // 4-8h
  if (remainingMinutes < 960) return { level: 3, ...URGENCY_MAP[3] }; // 8-16h
  if (remainingMinutes < 1440) return { level: 4, ...URGENCY_MAP[4] }; // 16-24h
  if (remainingMinutes < 2880) return { level: 5, ...URGENCY_MAP[5] }; // 24-48h
  return { level: 6, ...URGENCY_MAP[6] }; // >48h
};

/**
 * Gradient pairs for each urgency level (for SVG fills).
 * Each pair is [start, end] — a subtle darkening of the base color.
 */
export const SLA_URGENCY_GRADIENTS: Record<SLAUrgencyLevel, [string, string]> = {
  6: ['#d0c0f0', '#b8a8d8'],
  5: ['#b0d0e0', '#90b8cc'],
  4: ['#9acd32', '#7cb028'],
  3: ['#f0d000', '#d4b800'],
  2: ['#ff9000', '#e07e00'],
  1: ['#ff2020', '#e01010'],
  0: ['#cccccc', '#aaaaaa'],
};

/**
 * Get the urgency level for a terminal SLA state (breached, met, stopped).
 * All terminal states map to urgency 0 (grey).
 */
export const getTerminalSLAUrgency = (): SLAUrgency => ({
  level: 0,
  ...URGENCY_MAP[0],
});
